import React, { useEffect, useState } from 'react';
import {
  User, Mail, Lock, Trash2,
  Eye, EyeOff
} from 'lucide-react';
import usePageTitle from '../hooks/usePageTitle';
import '../css/ProfileSettings.css';
import { useAuth } from '../context/AuthContext';
import { changePassword, softDeleteUser, updateUser, USER_KEY } from '../helpers/user';

const ProfileSettings = () => {
  usePageTitle('Paramètres du profil');
  const { user, login, logout: contextLogout } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [messages, setMessages] = useState({
    profile: { error: '', success: '' },
    password: { error: '', success: '' }
  });

  const [formData, setFormData] = useState({
    username: user.username || '',
    email: user.email || ''
  });

  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  const [showPassword, setShowPassword] = useState({
    current: false,
    new: false,
    confirm: false
  });

  useEffect(() => {
    const hasMessage = Object.values(messages).some(
      form => form.error || form.success
    );

    if (hasMessage) {
      const timer = setTimeout(() => {
        setMessages({
          profile: { error: '', success: '' },
          password: { error: '', success: '' }
        });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [messages]);

  const setFormMessage = (form, type, message) => {
    setMessages(prev => ({
      ...prev,
      [form]: {
        error: type === 'error' ? message : '',
        success: type === 'success' ? message : ''
      }
    }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handlePasswordChange = (e) => {
    const { name, value } = e.target;
    setPasswordData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormMessage('profile', 'error', '');

    // Trim whitespace from inputs
    const trimmedUsername = formData.username.trim();
    const trimmedEmail = formData.email.trim();

    // Validation checks
    if (!trimmedUsername || !trimmedEmail) {
      setFormMessage('profile', 'error', 'Le nom d\'utilisateur et l\'email sont requis.');
      return;
    }

    // Basic email format validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(trimmedEmail)) {
      setFormMessage('profile', 'error', 'Veuillez entrer une adresse email valide.');
      return;
    }

    // Username length check (for example)
    if (trimmedUsername.length < 3) {
      setFormMessage('profile', 'error', 'Le nom d\'utilisateur doit contenir au moins 3 caractères.');
      return;
    }
    setIsLoading(true);

    try {
      const updatedUser = await updateUser(user.id, {
        username: trimmedUsername,
        email: trimmedEmail
      });
      if (updatedUser) {
        // Get the updated user data from localStorage to ensure consistency
        const userData = JSON.parse(localStorage.getItem(USER_KEY));
        // Update the AuthContext
        login(userData);
        setFormMessage('profile', 'success', 'Profil mis à jour avec succès!');
      }
    } catch (e) {
      console.error('Error updating user', e);
      setFormMessage('profile', 'error', 'Une erreur est survenue lors de la mise à jour du profil.');
    } finally {
      setIsLoading(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setFormMessage('password', 'error', '');

    // Validate current password is not empty
    if (!passwordData.currentPassword.trim()) {
      setFormMessage('password', 'error', 'Le mot de passe actuel est requis.');
      return;
    }

    // Validate new password
    if (!passwordData.newPassword.trim()) {
      setFormMessage('password', 'error', 'Le nouveau mot de passe est requis.');
      return;
    }

    // Check password minimum length (e.g., 8 characters)
    if (passwordData.newPassword.length < 8) {
      setFormMessage('password', 'error', 'Le nouveau mot de passe doit contenir au moins 8 caractères.');
      return;
    }

    // Validate password confirmation matches
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      setFormMessage('password', 'error', 'Les mots de passe ne correspondent pas.');
      return;
    }

    setIsLoading(true);

    try {
      const updatedUser = await changePassword({
        password: passwordData.newPassword,
        currentPassword: passwordData.currentPassword,
        passwordConfirmation: passwordData.confirmPassword
      });

      if (updatedUser) {
        setFormMessage('password', 'success', 'Mot de passe mis à jour avec succès!');
        // Clear password fields after successful update
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
      }
    } catch (e) {
      console.error('Error updating password:', e);
      setFormMessage('password', 'error', 'Une erreur est survenue lors de la mise à jour du mot de passe.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText.toLowerCase() !== 'supprimer') {
      setFormMessage('profile', 'error', 'Veuillez écrire "supprimer" pour confirmer.');
      return;
    }
    setShowDeleteModal(false);
    setIsLoading(true);
      try {
        const deleted = await softDeleteUser(user.id);
        if (deleted) {
          contextLogout();
          // window.location.href = '/connexion-inscription';
        } else {
          setFormMessage('profile', 'error', 'Une erreur est survenue lors de la suppression du compte.');
        }
      } catch (e) {
        console.error('Error deleting account:', e);
        setFormMessage('profile', 'error', 'Une erreur est survenue lors de la suppression du compte.');
      } finally {
        setIsLoading(false);
      }
  };

  return (
    <div className="profile-settings-page">
      <div className="profile-settings-hero">
        <div className="container">
          <h1>Paramètres du profil</h1>
        </div>
      </div>

      <div className="container">
        <div className="settings-grid">
          {/* Personal Information */}
          <div className="settings-section">
            <div className="section-card">
              <div className="section-header">
                <h3>Informations personnelles</h3>
              </div>
              <form onSubmit={handleSubmit} className="settings-form">
                {messages.profile.error && (
                  <div className="alert alert-danger" role="alert">
                    {messages.profile.error}
                  </div>
                )}
                {messages.profile.success && (
                  <div className="alert alert-success" role="alert">
                    {messages.profile.success}
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="username">Nom d'utilisateur</label>
                  <div className="input-wrapper">
                    <User size={20} />
                    <input
                      type="text"
                      id="username"
                      name="username"
                      value={formData.username}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="email">Email</label>
                  <div className="input-wrapper">
                    <Mail size={20} />
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>

                <button type="submit" className="submit-btn" disabled={isLoading}>
                  Mettre à jour le profil
                </button>
              </form>
            </div>
          </div>

          {/* Security Settings */}
          <div className="settings-section">
            {/* Change Password */}
            <div className="section-card">
              <div className="section-header">
                <h3>Sécurité</h3>
              </div>
              <form onSubmit={handlePasswordSubmit} className="settings-form">
                {messages.password.error && (
                  <div className="alert alert-danger" role="alert">
                    {messages.password.error}
                  </div>
                )}
                {messages.password.success && (
                  <div className="alert alert-success" role="alert">
                    {messages.password.success}
                  </div>
                )}
                <div className="form-group">
                  <label htmlFor="currentPassword">Mot de passe actuel</label>
                  <div className="input-wrapper">
                    <Lock size={20} />
                    <input
                      type={showPassword.current ? 'text' : 'password'}
                      id="currentPassword"
                      name="currentPassword"
                      value={passwordData.currentPassword}
                      onChange={handlePasswordChange}
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(prev => ({
                        ...prev,
                        current: !prev.current
                      }))}
                    >
                      {showPassword.current ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="newPassword">Nouveau mot de passe</label>
                  <div className="input-wrapper">
                    <Lock size={20} />
                    <input
                      type={showPassword.new ? 'text' : 'password'}
                      id="newPassword"
                      name="newPassword"
                      value={passwordData.newPassword}
                      onChange={handlePasswordChange}
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(prev => ({
                        ...prev,
                        new: !prev.new
                      }))}
                    >
                      {showPassword.new ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirmer le nouveau mot de passe</label>
                  <div className="input-wrapper">
                    <Lock size={20} />
                    <input
                      type={showPassword.confirm ? 'text' : 'password'}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={passwordData.confirmPassword}
                      onChange={handlePasswordChange}
                    />
                    <button
                      type="button"
                      className="toggle-password"
                      onClick={() => setShowPassword(prev => ({
                        ...prev,
                        confirm: !prev.confirm
                      }))}
                    >
                      {showPassword.confirm ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="submit-btn" disabled={isLoading}>
                  Changer le mot de passe
                </button>
              </form>
            </div>

            {/* Account Deletion */}
            <div className="section-card danger-zone">
              <div className="section-header">
                <h3>Zone de danger</h3>
                <Trash2 size={20} color="#dc3545" />
              </div>
              <div className="account-deletion">
                <div className="deletion-info">
                  <h4>Supprimer mon compte</h4>
                  <p>Cette action est définitive et supprimera toutes vos données.</p>
                </div>
                <button className="delete-account-btn" onClick={() => setShowDeleteModal(true)}>
                  <Trash2 size={20} />
                  Supprimer mon compte
                </button>
              </div>
            </div>
          </div>
{/* Delete Account Modal */}
{showDeleteModal && (
  <div className="modal show d-block" tabIndex="-1" role="dialog" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
    <div className="modal-dialog" role="document">
      <div className="modal-content">
        <div className="modal-header">
          <h5 className="modal-title">Supprimer le compte</h5>
          <button 
            type="button" 
            className="btn-close" 
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}
          />
        </div>
        <div className="modal-body">
          <p>Cette action est définitive et supprimera toutes vos données. Pour confirmer, écrivez "supprimer" ci-dessous.</p>
          <input
            type="text"
            className="form-control"
            value={deleteConfirmText}
            onChange={(e) => setDeleteConfirmText(e.target.value)}
            placeholder="Écrivez 'supprimer'"
          />
        </div>
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-secondary" 
            onClick={() => {
              setShowDeleteModal(false);
              setDeleteConfirmText('');
            }}
          >
            Annuler
          </button>
          <button 
            type="button" 
            className="btn btn-danger" 
            onClick={handleDeleteAccount}
            disabled={deleteConfirmText.toLowerCase() !== 'supprimer'}
          >
            Supprimer définitivement
          </button>
        </div>
      </div>
    </div>
  </div>
)}

        </div>
      </div>
    </div>
  );
};

export default ProfileSettings;